import {
  Add as AddIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  EditNote as EditNoteIcon,
  PauseCircleFilled as PauseCircleFilledIcon,
  People as CustomersIcon,
  RocketLaunch as RocketLaunchIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  GridLegacy as Grid,
  Switch,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  AdminEmptyState,
  AdminDialog,
  PageShell,
  AdminTableActions,
  AdminTableLoadingRow,
  AdminTablePanel,
} from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useTableSorting } from "@/hooks/useTableSorting";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import { CustomerEditDialog } from "@/pages/admin/organization/customers/CustomerEditDialog";

interface CustomerFormData {
  name: string;
  initial: string;
  saveForLaterTemplate: string;
  live: boolean;
  active: boolean;
}
type CustomerSortColumn = "id" | "initial" | "name" | "active" | "live";

const Customers: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const customerService = CustomerService.getInstance();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [nameError, setNameError] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<CustomerFormData>({
    name: "",
    initial: "",
    saveForLaterTemplate: "",
    live: false,
    active: true,
  });
  const visibleCustomers = showInactive
    ? customers
    : customers.filter((customer) => customer.active);
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    visibleCustomers,
    (customer, column) =>
      ({
        id: customer.id,
        initial: customer.initial,
        name: customer.name,
        active: customer.active,
        live: customer.live,
      })[column],
    "id" as CustomerSortColumn,
  );

  useEffect(() => {
    document.title = t("management.customers.customers.documentTitle");
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await customerService.getAllCustomers({ includeInactive: true });

      if (response.success && response.data) {
        setCustomers(response.data);
      } else {
        setError(response.error || t("management.customers.customers.errors.failedFetchCustomers"));
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t("management.customers.customers.errors.unexpectedFetchCustomers"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setDialogError(null);
    const hasNameError = !formData.name.trim();
    setNameError(hasNameError);
    if (hasNameError) {
      nameInputRef.current?.focus();
      return;
    }
    setIsLoading(true);

    try {
      const response = await customerService.addCustomer(formData);
      if (response.success && response.data) {
        setCustomers((prev) => [...prev, response.data as Customer]);
        setIsModalOpen(false);
        resetForm();
      } else {
        setDialogError(
          response.error || t("management.customers.customers.errors.failedCreateCustomer"),
        );
      }
    } catch (err) {
      setDialogError(
        err instanceof Error
          ? err.message
          : t("management.customers.customers.errors.errorCreatingCustomer"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      initial: "",
      saveForLaterTemplate: "",
      live: false,
      active: true,
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "radio" ? value === "true" : value,
    }));
    if (name === "name" && value.trim()) setNameError(false);
  };

  const closeAddDialog = () => {
    setIsModalOpen(false);
    setDialogError(null);
    setNameError(false);
  };

  const navigateToNotes = (customerId: number) => {
    navigate(`/admin/organization/customers/notes/${customerId}`);
  };

  const navigateToConfig = (customerId: number) => {
    void navigate(`/admin/configuration/configs/edit/${customerId}`);
  };

  const renderActiveIcon = (customer: Customer) => (
    <Tooltip
      title={
        customer.active
          ? t("management.customers.customers.fields.active")
          : t("commandCenter.status.inactive")
      }
    >
      <Box component="span" sx={{ display: "inline-flex" }}>
        {customer.active ? (
          <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
        ) : (
          <CancelIcon color="error" sx={{ fontSize: 18 }} />
        )}
      </Box>
    </Tooltip>
  );

  const renderLiveIcon = (customer: Customer) => (
    <Tooltip
      title={
        customer.live
          ? t("management.customers.customers.fields.live")
          : t("management.customers.customers.fields.nonLive")
      }
    >
      <Box component="span" sx={{ display: "inline-flex" }}>
        {customer.live ? (
          <RocketLaunchIcon color="success" sx={{ fontSize: 18 }} />
        ) : (
          <PauseCircleFilledIcon color="disabled" sx={{ fontSize: 18 }} />
        )}
      </Box>
    </Tooltip>
  );

  const renderMobileView = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {customers.length > 0 ? (
        customers.map((customer) => (
          <Card key={customer.id} sx={{ width: "100%" }}>
            <CardContent>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t("management.customers.customers.text.customerId", {
                      customerId: customer.id,
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" component="div">
                    {customer.name}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    {t("management.customers.customers.fields.initial")}
                  </Typography>
                  <Typography variant="body2">{customer.initial ?? "—"}</Typography>
                </Grid>
                {showInactive && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      {t("management.customers.customers.fields.active")}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>{renderActiveIcon(customer)}</Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    {t("management.customers.customers.fields.live")}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>{renderLiveIcon(customer)}</Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button
                      startIcon={<EditNoteIcon />}
                      size="small"
                      onClick={() => navigateToNotes(customer.id)}
                    >
                      {t("management.customers.customers.actions.notes")}
                    </Button>
                    <Button
                      startIcon={<SettingsIcon />}
                      size="small"
                      onClick={() => navigateToConfig(customer.id)}
                    >
                      {t("operations.configs.configs.actions.editConfiguration")}
                    </Button>
                    <Button
                      startIcon={<EditIcon />}
                      size="small"
                      onClick={() => setEditingCustomer(customer)}
                    >
                      {t("management.customers.customers.actions.edit")}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))
      ) : (
        <Card>
          <CardContent>
            <AdminEmptyState
              icon={<CustomersIcon sx={{ fontSize: 40 }} />}
              text={t("common.noDataAvailable")}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const renderDesktopView = () => (
    <AdminTablePanel
      header={
        <Box sx={{ display: "flex", justifyContent: "end" }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(event) => setShowInactive(event.target.checked)}
              />
            }
            label={t("management.customers.customers.actions.showInactive")}
          />
        </Box>
      }
    >
      <TableHead>
        <TableRow>
          <SortableTableCell
            activeColumn={sortColumn}
            column="id"
            direction={sortDirection}
            onSort={handleSort}
            sx={{ width: "1%", whiteSpace: "nowrap" }}
          >
            {t("management.customers.customers.fields.id")}
          </SortableTableCell>
          <SortableTableCell
            activeColumn={sortColumn}
            column="live"
            direction={sortDirection}
            onSort={handleSort}
            sx={{
              width: "1%",
              whiteSpace: "nowrap",
              textAlign: "center",
            }}
          >
            {t("management.customers.customers.fields.live")}
          </SortableTableCell>
          <SortableTableCell
            activeColumn={sortColumn}
            column="initial"
            direction={sortDirection}
            onSort={handleSort}
            sx={{ width: 100, whiteSpace: "nowrap", textAlign: "center" }}
          >
            {t("management.customers.customers.fields.initial")}
          </SortableTableCell>
          <SortableTableCell
            activeColumn={sortColumn}
            column="name"
            direction={sortDirection}
            onSort={handleSort}
          >
            {t("management.customers.customers.fields.customerName")}
          </SortableTableCell>
          {showInactive ? (
            <SortableTableCell
              activeColumn={sortColumn}
              column="active"
              direction={sortDirection}
              onSort={handleSort}
              sx={{
                width: 100,
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {t("management.customers.customers.fields.active")}
            </SortableTableCell>
          ) : null}
          <TableCell sx={{ width: "1%", whiteSpace: "nowrap", textAlign: "end" }}>
            {t("management.customers.customers.fields.actions")}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <AdminTableLoadingRow colSpan={showInactive ? 6 : 5} label={t("common.loading")} />
        ) : sortedItems.length > 0 ? (
          sortedItems.map((customer) => (
            <TableRow key={customer.id}>
              <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>{customer.id}</TableCell>
              <TableCell sx={{ width: "1%", textAlign: "center", whiteSpace: "nowrap" }}>
                {renderLiveIcon(customer)}
              </TableCell>
              <TableCell sx={{ width: 100, textAlign: "center" }}>
                {customer.initial ?? "—"}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{customer.name}</TableCell>
              {showInactive && (
                <TableCell sx={{ width: 100, textAlign: "center" }}>
                  {renderActiveIcon(customer)}
                </TableCell>
              )}
              <TableCell sx={{ width: "1%", whiteSpace: "nowrap", textAlign: "end" }}>
                <AdminTableActions
                  actions={[
                    {
                      icon: <EditNoteIcon />,
                      hint: t("management.customers.customers.actions.editNotes"),
                      action: () => navigateToNotes(customer.id),
                    },
                    {
                      icon: <SettingsIcon />,
                      hint: t("operations.configs.configs.tooltips.editConfiguration"),
                      action: () => navigateToConfig(customer.id),
                    },
                    {
                      icon: <EditIcon />,
                      hint: t("management.customers.customers.actions.edit"),
                      action: () => setEditingCustomer(customer),
                    },
                  ]}
                />
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={showInactive ? 6 : 5} align="center">
              <AdminEmptyState
                icon={<CustomersIcon sx={{ fontSize: 40 }} />}
                text={t("common.noDataAvailable")}
              />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </AdminTablePanel>
  );

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.customers.customers.title")}
      icon={<CustomersIcon />}
      titleAccessory={<LoadingRing loading={isLoading} />}
      headerActions={
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setDialogError(null);
            setNameError(false);
            setIsModalOpen(true);
          }}
        >
          {t("management.customers.customers.actions.addCustomer")}
        </Button>
      }
    >
      <Box sx={{ mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isMobile ? renderMobileView() : renderDesktopView()}
      </Box>

      <AdminDialog
        open={isModalOpen}
        onClose={closeAddDialog}
        maxWidth="sm"
        title={t("management.customers.customers.dialogs.addCustomerTitle")}
        icon={<CustomersIcon />}
        error={dialogError}
        errorOperation="create"
        errorEntity={t("common.entities.customer")}
        cancelAction={{ label: t("common.cancel"), onClick: closeAddDialog }}
        primaryAction={{
          label: t("management.customers.customers.actions.add"),
          onClick: () => void handleSubmit(),
          loading: isLoading,
        }}
      >
        <form noValidate onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t("management.customers.customers.fields.customerName")}
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            inputRef={nameInputRef}
            error={nameError}
            helperText={nameError ? t("common.required") : undefined}
            required
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("management.customers.customers.fields.initial")}
            name="initial"
            value={formData.initial}
            onChange={handleInputChange}
            inputProps={{ maxLength: 10 }}
            margin="normal"
          />
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.live}
                    onChange={(event) =>
                      setFormData((current) => ({ ...current, live: event.target.checked }))
                    }
                  />
                }
                label={t("management.customers.customers.fields.live")}
              />
              <Typography variant="body2" color="text.secondary">
                {t("management.customers.customers.hints.live")}
              </Typography>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={(event) =>
                      setFormData((current) => ({ ...current, active: event.target.checked }))
                    }
                  />
                }
                label={t("management.customers.customers.fields.active")}
              />
              <Typography variant="body2" color="text.secondary">
                {t("management.customers.customers.hints.active")}
              </Typography>
            </Box>
          </Box>
        </form>
      </AdminDialog>
      <CustomerEditDialog
        customer={editingCustomer}
        onClose={() => setEditingCustomer(null)}
        onSuccess={(updatedCustomer) =>
          setCustomers((current) =>
            current.map((customer) =>
              customer.id === updatedCustomer.id ? updatedCustomer : customer,
            ),
          )
        }
      />
    </PageShell>
  );
};

export default Customers;
